import React from 'react';

interface PageHeaderProps {
  title?: string;
  subtitle?: string;
  /** Renders under the title (e.g. cross-page nav). */
  underTitle?: React.ReactNode;
  /** Right-side slot — pass filter controls here */
  filters?: React.ReactNode;
  /**
   * Full custom inner row (only the sticky chrome is shared).
   * When set, title / subtitle / filters are ignored.
   */
  children?: React.ReactNode;
}

/**
 * Sticky per-page header bar.
 * Title lives on the left; filters/actions slot on the right.
 * Replaces the old global TopBar — each page owns its header.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title = '',
  subtitle,
  underTitle,
  filters,
  children,
}) => {
  if (children != null) {
    return (
      <div className="sticky top-0 z-30 bg-slate-50/95 backdrop-blur-sm border-b border-slate-200 shadow-[0_1px_6px_0_rgba(0,0,0,0.08)]">
        <div className="px-6 py-2 min-w-0">{children}</div>
      </div>
    );
  }

  return (
    <div className="sticky top-0 z-30 bg-slate-50/95 backdrop-blur-sm border-b border-slate-200 shadow-[0_1px_6px_0_rgba(0,0,0,0.08)]">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-6 min-h-[3.5rem] py-2 min-w-0">
        <div className="flex-none min-w-0">
          <h1 className="text-[15px] font-semibold text-[#0D0D0D] leading-snug truncate">
            {title}
          </h1>
          {underTitle ? (
            <div className="mt-1.5 min-w-0">{underTitle}</div>
          ) : null}
          {subtitle ? (
            <p className="text-[11px] text-slate-500 mt-0.5 leading-snug truncate">
              {subtitle}
            </p>
          ) : null}
        </div>

        {filters && <div className="h-5 w-px bg-slate-200 flex-none" />}

        {filters && (
          <div className="flex-1 min-w-0 flex items-center justify-end">
            {filters}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
